import { useTranslation } from "react-i18next";
import {
  ScanSearch, PencilLine, ShieldCheck, DatabaseZap, Image as ImageIcon,
  TriangleAlert, Repeat2, Clock, FileCode2,
} from "lucide-react";

const FIELD_LABEL_KEY = {
  raw_material_form: "extractionConfirm.rawForm",
  processes: "extractionConfirm.processes",
  external_diameters: "extractionConfirm.externalDiameters",
  internal_diameters: "extractionConfirm.internalDiameters",
  threads: "extractionConfirm.threads",
  overall_dimensions_mm: "extractionConfirm.overall",
  overall_dimensions: "extractionConfirm.overall",
};

const VALUE_NS = {
  raw_material_form: "extractionConfirm.forms",
  processes: "extractionConfirm.proc",
};

const fieldLabel = (field, t) =>
  FIELD_LABEL_KEY[field] ? t(FIELD_LABEL_KEY[field]) : field;

// "block" → "Blocchetto massiccio"; "drilling, milling" → "Foratura, Fresatura"
const valueLabel = (field, raw, t) => {
  const ns = VALUE_NS[field];
  if (!ns || !raw || raw === "—") return raw || "—";
  return String(raw)
    .split(",")
    .map((tok) => {
      const k = tok.trim();
      const tr = t(`${ns}.${k}`, { defaultValue: "" });
      return tr || k;
    })
    .join(", ");
};

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

function Kpi({ icon, label, value, accent, testid }) {
  const cls = accent === "amber" ? "text-amber-400"
    : accent === "emerald" ? "text-emerald-400"
    : accent === "rose" ? "text-rose-400" : "text-slate-100";
  return (
    <div className="tech-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 mb-2 text-xs">{icon}<span className="label-tech">{label}</span></div>
      <div className={`font-mono-data text-2xl ${cls}`}>{value}</div>
    </div>
  );
}

function ConfidenceChips({ confidence, t }) {
  const order = ["user_confirmed", "high", "medium", "low"];
  const tone = {
    user_confirmed: "border-emerald-500/40 text-emerald-300",
    high: "border-sky-500/40 text-sky-300",
    medium: "border-amber-500/40 text-amber-300",
    low: "border-rose-500/40 text-rose-300",
  };
  const rows = order.filter((k) => (confidence || {})[k]);
  if (!rows.length) return <span className="text-slate-600">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {rows.map((k) => (
        <span key={k} className={`px-1.5 py-0.5 rounded border text-[10px] font-mono-data ${tone[k]}`}>
          {t(`extractionConfirm.conf.${k}`)} {confidence[k]}
        </span>
      ))}
    </div>
  );
}

export default function AdminExtractionQualityTab({ data, days, onDaysChange }) {
  const { t } = useTranslation();
  const tot = data?.totals || {};
  const fields = data?.fields || [];
  const patterns = data?.patterns || [];
  const recent = data?.recent || [];
  const maxEdits = Math.max(1, ...fields.map((f) => f.edits));

  return (
    <div className="space-y-6" data-testid="admin-extraction-quality">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-display text-slate-100">{t("adminExtraction.title")}</h2>
          <p className="text-sm text-slate-400 max-w-3xl mt-1">{t("adminExtraction.subtitle")}</p>
        </div>
        <div className="flex gap-1" data-testid="extraction-window-switch">
          {[30, 90, 180].map((d) => (
            <button key={d} onClick={() => onDaysChange(d)}
              className={`px-3 py-1.5 text-xs rounded border font-mono-data transition-colors ${
                days === d ? "border-amber-500 text-amber-300 bg-amber-500/10"
                  : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
              data-testid={`extraction-window-${d}`}>
              {t("adminExtraction.windowDays", { n: d })}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi testid="eq-kpi-extractions" icon={<ScanSearch size={14}/>} label={t("adminExtraction.kpiExtractions")} value={tot.extractions ?? 0}/>
        <Kpi testid="eq-kpi-clean" icon={<ShieldCheck size={14}/>} label={t("adminExtraction.kpiCleanRate")} value={`${tot.clean_rate_pct ?? 0}%`} accent="emerald"/>
        <Kpi testid="eq-kpi-edits" icon={<PencilLine size={14}/>} label={t("adminExtraction.kpiEdits")} value={tot.edits_total ?? 0} accent="amber"/>
        <Kpi testid="eq-kpi-edits-avg" icon={<PencilLine size={14}/>} label={t("adminExtraction.kpiEditsPerExtraction")} value={tot.edits_per_extraction ?? 0}/>
        <Kpi testid="eq-kpi-cache" icon={<DatabaseZap size={14}/>} label={t("adminExtraction.kpiCacheHit")} value={`${tot.cache_hit_rate_pct ?? 0}%`}/>
        <Kpi testid="eq-kpi-raster" icon={<ImageIcon size={14}/>} label={t("adminExtraction.kpiRaster")} value={`${tot.raster_share_pct ?? 0}%`}/>
      </div>

      {!tot.extractions && (
        <div className="tech-card p-6 text-center" data-testid="eq-empty">
          <div className="text-slate-300 text-sm">{t("adminExtraction.empty")}</div>
          <div className="text-slate-500 text-xs mt-1">{t("adminExtraction.emptyHint")}</div>
        </div>
      )}

      {!!fields.length && (
        <div className="tech-card p-4" data-testid="eq-fields-card">
          <div className="label-tech text-slate-400 mb-1">{t("adminExtraction.fieldsTitle")}</div>
          <p className="text-xs text-slate-500 mb-3">{t("adminExtraction.fieldsHint")}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="py-2 pr-3">{t("adminExtraction.thField")}</th>
                  <th className="py-2 pr-3">{t("adminExtraction.thEdits")}</th>
                  <th className="py-2 pr-3">{t("adminExtraction.thEditRate")}</th>
                  <th className="py-2 pr-3">{t("adminExtraction.thStage")}</th>
                  <th className="py-2 pr-3">{t("adminExtraction.thOverconfident")}</th>
                  <th className="py-2 pr-3">{t("adminExtraction.thConfidence")}</th>
                  <th className="py-2">{t("adminExtraction.thLastEdit")}</th>
                </tr>
              </thead>
              <tbody className="font-mono-data text-xs">
                {fields.map((f) => (
                  <tr key={f.field} className="border-b border-slate-800/60" data-testid={`eq-field-row-${f.field}`}>
                    <td className="py-2 pr-3 font-sans text-slate-200">{fieldLabel(f.field, t)}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="text-amber-300 w-6">{f.edits}</span>
                        <span className="h-1.5 rounded-full bg-amber-500/60 inline-block"
                          style={{ width: `${Math.max(6, (f.edits / maxEdits) * 90)}px` }}/>
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-slate-300">{f.edit_rate_pct}%</td>
                    <td className="py-2 pr-3 text-slate-400">
                      {f.pre_quote} / {f.post_quote}
                    </td>
                    <td className="py-2 pr-3">
                      {f.overconfident_edits > 0
                        ? <span className="text-rose-300 flex items-center gap-1"><TriangleAlert size={12}/>{f.overconfident_edits}</span>
                        : <span className="text-slate-600">0</span>}
                    </td>
                    <td className="py-2 pr-3"><ConfidenceChips confidence={f.confidence} t={t}/></td>
                    <td className="py-2 text-slate-400">{shortDate(f.last_edit_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-1.5">
            <TriangleAlert size={12} className="mt-0.5 text-rose-400 shrink-0"/>{t("adminExtraction.overconfidentHint")}
          </p>
        </div>
      )}

      {!!patterns.length && (
        <div className="tech-card p-4" data-testid="eq-patterns-card">
          <div className="label-tech text-slate-400 mb-1 flex items-center gap-2"><Repeat2 size={14}/>{t("adminExtraction.patternsTitle")}</div>
          <p className="text-xs text-slate-500 mb-3">{t("adminExtraction.patternsHint")}</p>
          <div className="space-y-2">
            {patterns.map((p, i) => (
              <div key={`${p.field}-${i}`} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`eq-pattern-${i}`}>
                <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-sans">{fieldLabel(p.field, t)}</span>
                <span className="font-mono-data text-rose-300 line-through decoration-rose-500/50">{valueLabel(p.field, p.ai_value, t)}</span>
                <span className="text-slate-500">→</span>
                <span className="font-mono-data text-emerald-300">{valueLabel(p.field, p.user_value, t)}</span>
                <span className="ml-auto font-mono-data text-amber-300">×{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {!!recent.length && (
          <div className="tech-card p-4" data-testid="eq-recent-card">
            <div className="label-tech text-slate-400 mb-3 flex items-center gap-2"><Clock size={14}/>{t("adminExtraction.recentTitle")}</div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {recent.map((r, i) => (
                <div key={`${r.at}-${r.field}-${r.quote_id || ""}`} className="text-xs border-l-2 border-slate-700 pl-2" data-testid={`eq-recent-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-200 font-sans">{fieldLabel(r.field, t)}</span>
                    <span className={`px-1 rounded text-[10px] font-mono-data ${r.stage === "post_quote" ? "bg-amber-500/15 text-amber-300" : "bg-slate-800 text-slate-400"}`}>
                      {r.stage === "post_quote" ? t("adminExtraction.stagePost") : t("adminExtraction.stagePre")}
                    </span>
                    <span className="ml-auto text-slate-500 font-mono-data">{shortDate(r.at)}</span>
                  </div>
                  <div className="font-mono-data text-[11px] mt-0.5">
                    <span className="text-rose-300">{valueLabel(r.field, r.ai_value, t)}</span>
                    <span className="text-slate-500"> → </span>
                    <span className="text-emerald-300">{valueLabel(r.field, r.user_value, t)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="tech-card p-4" data-testid="eq-formats-card">
            <div className="label-tech text-slate-400 mb-3">{t("adminExtraction.formatsTitle")}</div>
            <table className="w-full text-xs font-mono-data">
              <thead className="text-left text-[11px] uppercase text-slate-500">
                <tr><th className="pb-2">{t("adminExtraction.thFormat")}</th><th className="pb-2">{t("adminExtraction.kpiExtractions")}</th><th className="pb-2">{t("adminExtraction.thEdits")}</th></tr>
              </thead>
              <tbody>
                {(data?.by_source_format || []).map((r) => (
                  <tr key={r.format} className="border-t border-slate-800/60">
                    <td className="py-1.5 text-slate-200">{r.format}</td>
                    <td className="py-1.5 text-slate-300">{r.extractions}</td>
                    <td className="py-1.5 text-amber-300">{r.edits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tech-card p-4" data-testid="eq-prompt-card">
            <div className="label-tech text-slate-400 mb-1 flex items-center gap-2"><FileCode2 size={14}/>{t("adminExtraction.promptTitle")}</div>
            <p className="text-xs text-slate-500 mb-3">{t("adminExtraction.promptHint")}</p>
            <table className="w-full text-xs font-mono-data">
              <thead className="text-left text-[11px] uppercase text-slate-500">
                <tr><th className="pb-2">{t("adminExtraction.thPrompt")}</th><th className="pb-2">{t("adminExtraction.kpiExtractions")}</th><th className="pb-2">{t("adminExtraction.thCleanRate")}</th></tr>
              </thead>
              <tbody>
                {(data?.by_prompt_version || []).map((r) => (
                  <tr key={r.prompt_version} className="border-t border-slate-800/60" data-testid={`eq-prompt-${r.prompt_version}`}>
                    <td className="py-1.5 text-slate-200">{r.prompt_version}</td>
                    <td className="py-1.5 text-slate-300">{r.extractions}</td>
                    <td className="py-1.5 text-emerald-300">{r.clean_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
